/**
 * Document Sharing Helper
 *
 * Pre-fetches shared record IDs for a user, used by scopedQuery/scopedGet
 * to include records shared with the user even if outside their branch/scope restrictions.
 */

import { eq, and, or, isNull, sql } from "drizzle-orm";

/**
 * Get record IDs that are shared with a specific user for a module+doctype.
 * Call this BEFORE scopedQuery/scopedGet to get the shared IDs.
 *
 * @param db - Drizzle instance
 * @param sharesTable - The recordShares table reference (passed to avoid circular deps)
 * @param userId - The user to check shares for
 * @param module - Module name (e.g., "car-rental")
 * @param doctype - Doctype name (e.g., "bookings")
 * @returns Array of record IDs shared with this user
 */
export async function getSharedRecordIds(
  db: any,
  sharesTable: any,
  userId: string,
  module: string,
  doctype: string,
): Promise<string[]> {
  const now = new Date().toISOString();

  const shares = await db
    .select({ recordId: sharesTable.recordId })
    .from(sharesTable)
    .where(
      and(
        eq(sharesTable.userId, userId),
        eq(sharesTable.module, module),
        eq(sharesTable.doctype, doctype),
        eq(sharesTable.canRead, 1),
        or(
          isNull(sharesTable.expiresAt),
          sql`${sharesTable.expiresAt} > ${now}`,
        ),
      ),
    )
    .all();

  return shares.map((s: any) => s.recordId);
}

/**
 * Check if a specific record is shared with a user (with write permission).
 *
 * @param db - Drizzle instance
 * @param sharesTable - The recordShares table reference
 * @param userId - The user to check
 * @param module - Module name
 * @param doctype - Doctype name
 * @param recordId - The specific record ID
 * @returns Object with canRead and canWrite flags
 */
export async function getSharePermission(
  db: any,
  sharesTable: any,
  userId: string,
  module: string,
  doctype: string,
  recordId: string,
): Promise<{ canRead: boolean; canWrite: boolean } | null> {
  const now = new Date().toISOString();

  const share = await db
    .select({
      canRead: sharesTable.canRead,
      canWrite: sharesTable.canWrite,
    })
    .from(sharesTable)
    .where(
      and(
        eq(sharesTable.userId, userId),
        eq(sharesTable.module, module),
        eq(sharesTable.doctype, doctype),
        eq(sharesTable.recordId, recordId),
        or(
          isNull(sharesTable.expiresAt),
          sql`${sharesTable.expiresAt} > ${now}`,
        ),
      ),
    )
    .get();

  if (!share) return null;

  return {
    canRead: share.canRead === 1,
    canWrite: share.canWrite === 1,
  };
}
